import { AddCircleOutline } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { invalidateServiceCache, useAddService } from '../../../../api/frequency'
import { ShowDialogProps } from '../../../../show-dialog'
import { FrequencyTable } from './FrequenciesTable'
import { FrequencyForm } from './FrequencyForm'

interface CreateFrequencyProps extends ShowDialogProps {
  customerId: EntityId
  serviceId: EntityId
}

export const CreateFrequencyDialog: React.FC<CreateFrequencyProps> = ({ pop, customerId, serviceId }) => {
  const { t } = useTranslation(['frequency', 'common', 'service'])

  const { mutateAsync } = useAddService(customerId as EntityId)
  const onSubmit = async form => {
    const data = await mutateAsync(form, {
      onSuccess: () => {
        toast.success(t('serviceAddedSuccessfully', { ns: 'service' }))
        invalidateServiceCache.getServices(customerId as EntityId)
        pop()
      },
      onError: _ => {
        toast.error(t('addingError', { ns: 'common' }))
      },
    })
    return data
  }

  return <FrequencyForm onSubmit={onSubmit} onCancel={() => pop()} title={t('addservice', { ns: 'service' })} />
}

export const FrequenciesContainer = () => {
  const { t: translate } = useTranslation(['frequency', 'common'])
  const { id: customerId } = useParams()
  const theme = useTheme()
  const { showDialog } = useDialogCtx()

  const onAddFrequency = async () => {
    await showDialog(
      CreateFrequencyDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }

  return (
    <CommonPageLayout
      titleSection={{ subtitle: translate('service-frequencies', { ns: 'service' }) }}
      topAction={[
        {
          buttonText: translate('addservice', { ns: 'service' }),
          buttonIcon: <AddCircleOutline />,
          onClick: onAddFrequency,
          variant: 'outlined',
          shadowWeight: null,
          border: 0,
        },
      ]}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <FrequencyTable customerId={customerId as EntityId} />
      </Box>
    </CommonPageLayout>
  )
}
